import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableToken,
  IS_IN_EDITOR_FRAME,
  useContentToken,
  usePropertyField,
} from '@vibesites/runtime/react';

/**
 * Property-local Engrain Sightmap embed.
 *
 * The catalog `engrain-sitemap` block always mounts a full-bleed Sightmap
 * iframe. Inside the nested editor preview that iframe paints black and
 * swallows the page when edit mode is entered. Mirror the AudioEye pattern:
 * show a light placeholder in the editor frame, and the live iframe on the
 * deployed site.
 */
function EditorFields({
  sitemapUrl,
  sitemapId,
  height,
}: {
  sitemapUrl: string;
  sitemapId: string;
  height: string;
}) {
  return (
    <div className="mt-6 space-y-2 text-left text-sm">
      <label className="block">
        <span className="mb-1 block font-medium text-[var(--vibe-color-text,#111827)]">
          Sightmap URL
        </span>
        <div className="rounded border border-[var(--vibe-color-border,#d1d5db)] bg-white px-3 py-2">
          <EditableToken
            path="interactivemapembed.sitemap_url"
            as="span"
            className="block break-all"
          >
            {sitemapUrl || '—'}
          </EditableToken>
        </div>
      </label>
      <label className="block">
        <span className="mb-1 block font-medium text-[var(--vibe-color-text,#111827)]">
          Sightmap ID
        </span>
        <div className="rounded border border-[var(--vibe-color-border,#d1d5db)] bg-white px-3 py-2">
          <EditableToken
            path="interactivemapembed.sitemap_id"
            as="span"
            className="block break-all"
          >
            {sitemapId || '—'}
          </EditableToken>
        </div>
      </label>
      <label className="block">
        <span className="mb-1 block font-medium text-[var(--vibe-color-text,#111827)]">
          Height (px)
        </span>
        <div className="rounded border border-[var(--vibe-color-border,#d1d5db)] bg-white px-3 py-2">
          <EditableToken path="interactivemapembed.height" as="span" className="block">
            {height || '1000'}
          </EditableToken>
        </div>
      </label>
    </div>
  );
}

function HiddenFieldEditors({
  sitemapUrl,
  sitemapId,
  height,
  title,
}: {
  sitemapUrl: string;
  sitemapId: string;
  height: string;
  title: string;
}) {
  return (
    <div className="sr-only" aria-hidden="true">
      <EditableToken path="interactivemapembed.sitemap_id" as="span">
        {sitemapId}
      </EditableToken>
      <EditableToken path="interactivemapembed.sitemap_url" as="span">
        {sitemapUrl}
      </EditableToken>
      <EditableToken path="interactivemapembed.height" as="span">
        {height}
      </EditableToken>
      <EditableToken path="interactivemapembed.title" as="span">
        {title}
      </EditableToken>
    </div>
  );
}

export function InteractiveMapEmbed() {
  const sitemapIdToken = useContentToken('interactivemapembed.sitemap_id');
  const sitemapUrlToken = useContentToken('interactivemapembed.sitemap_url');
  // Legacy field names kept as read-only fallbacks (hooks must always run).
  const legacySitemapId = usePropertyField('custom_engrain_sitemap_id') as string | null;
  const legacySitemapUrl = usePropertyField('custom_engrain_sitemap_url') as string | null;
  const height = useContentToken('interactivemapembed.height');
  const title = useContentToken('interactivemapembed.title');

  const sitemapId = sitemapIdToken || legacySitemapId || '';
  const sitemapUrl = sitemapUrlToken || legacySitemapUrl || '';
  const resolvedHeight = height || '1000';
  const resolvedTitle = title || 'Engrain Sightmap';
  const src =
    sitemapUrl || (sitemapId ? `https://sightmap.com/embed/${sitemapId}` : null);

  if (IS_IN_EDITOR_FRAME || !src) {
    return (
      <section
        className="flex items-center justify-center px-6 py-16 text-center"
        style={{
          minHeight: `${Math.min(Number(resolvedHeight) || 1000, 420)}px`,
          background: 'var(--vibe-color-background, #ffffff)',
          color: 'var(--vibe-color-text-muted, #4b5563)',
        }}
      >
        <div className="w-full max-w-xl rounded-lg border-2 border-dashed border-[var(--vibe-color-border,#d1d5db)] px-6 py-10">
          <EditableToken
            path="interactivemapembed.title"
            as="p"
            className="text-lg font-medium"
            style={{
              fontFamily: 'var(--vibe-font-heading)',
              color: 'var(--vibe-color-text, #111827)',
            }}
          >
            {resolvedTitle}
          </EditableToken>
          <p className="mt-2 text-sm">
            {IS_IN_EDITOR_FRAME
              ? 'The live Engrain Sightmap appears here on the deployed site. It is hidden in the editor because the nested preview iframe cannot render the map reliably.'
              : 'Add an Engrain Sightmap ID or URL to load the interactive map.'}
          </p>
          <EditorFields
            sitemapUrl={sitemapUrl}
            sitemapId={sitemapId}
            height={resolvedHeight}
          />
        </div>
      </section>
    );
  }

  return (
    <section>
      <HiddenFieldEditors
        sitemapUrl={sitemapUrl}
        sitemapId={sitemapId}
        height={resolvedHeight}
        title={resolvedTitle}
      />
      <iframe
        src={src}
        title={resolvedTitle}
        width="100%"
        height={resolvedHeight}
        style={{ border: 'none', display: 'block' }}
        loading="lazy"
        allow="geolocation; web-share; clipboard-write"
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapEmbed',
  displayName: 'Interactive Map Embed',
  tokens: {
    'interactivemapembed.sitemap_id': {
      label: 'Sightmap ID',
      type: 'text',
      defaultField: 'custom_engrain_sightmap_id',
      suggestedFields: ['custom_engrain_sightmap_id'],
      description: 'Engrain sightmap identifier appended to the embed URL.',
    },
    'interactivemapembed.sitemap_url': {
      label: 'Sightmap URL',
      type: 'url',
      defaultField: 'custom_engrain_sightmap_url',
      suggestedFields: ['custom_engrain_sightmap_url'],
      description:
        'Full Engrain embed URL (e.g. https://sightmap.com/embed/yzvg8lq7pln). Takes precedence over Sightmap ID.',
    },
    'interactivemapembed.height': {
      label: 'Height (px)',
      type: 'text',
      defaultValue: '1000',
      description: 'Iframe height in pixels.',
    },
    'interactivemapembed.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Engrain Sightmap',
      description: 'Accessible iframe title.',
    },
  },
  contentRules: [
    {
      kind: 'oneOf',
      tokens: [
        'interactivemapembed.sitemap_id',
        'interactivemapembed.sitemap_url',
      ],
      message: 'Add a Sightmap ID or a Sightmap URL.',
    },
  ],
};

(InteractiveMapEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InteractiveMapEmbed;
